// # 보기를 화면에 꽂는다 — 다크 모드·표시는 기기에서, 테마·언어는 서버에서
//
// 다크 모드는 기기의 것이다(슬랙 360019434914). `platform.keep` 에 두고 로그인 전에도
// 건다 — 로그인 화면만 하얗게 번쩍이면 다크 모드를 켠 사람에게는 섬광이다.
// 테마·강조색·언어는 조직의 보기 설정(`/v1/me/appearance`)이라 들어온 뒤에 건다.
//
// ## 꽂는 자리는 뿌리 요소 하나
// `color-scheme` 은 `light-dark()` 가 읽는 값이라 뿌리에 `data-scheme` 하나로 갈린다.
// 표시는 `data-display` 의 낱말(`compact`·`sm`·`lg`·`calm`)로 토큰을 갈아 끼운다(`display.ts`).
// 테마는 `data-theme`, 사용자 강조색은 뿌리의 `--accent` 를 덮는다(모드 둘 다 같은 색).
// 스타일 규칙은 `style.css` 의 `tokens` 층에 산다 — 여기서 색 값을 만들지 않는다.

import type { Keeper } from '@maru/platform'
import type { Look, Scheme } from '@maru/ui'

/** 기기에 두는 열쇠. */
export const SCHEME_KEY = 'maru.scheme'
export const DISPLAY_KEY = 'maru.display'

/** 뿌리 요소에서 우리가 만지는 만큼. */
export interface Root {
  dataset: Record<string, string | undefined>
  style: { setProperty(name: string, value: string): void; removeProperty(name: string): void }
  lang: string
}

export function read_scheme(raw: string | null): Scheme {
  return raw === 'light' || raw === 'dark' ? raw : 'system'
}

export async function load_scheme(keep: Keeper): Promise<Scheme> {
  try {
    return read_scheme(await keep.get(SCHEME_KEY))
  } catch {
    return 'system'
  }
}

/** 저장은 던질 수 있다. 다크 모드는 편의라 못 적으면 이번 창에서만 산다. */
export async function keep_scheme(keep: Keeper, s: Scheme): Promise<void> {
  try {
    if (s === 'system') await keep.remove(SCHEME_KEY)
    else await keep.put(SCHEME_KEY, s)
  } catch {
    // 이 창이 열려 있는 동안은 꽂힌 값이 산다.
  }
}

/**
 * 색을 갈아 끼우는 두 프레임 동안 옮겨 가는 효과를 끈다(`style.css` 의 `[data-swap]`). 칸마다 제 속도로 색이
 * 바뀌어 모드를 바꾸는 순간 카드만 회색으로 남은 얼룩진 화면이 한 박자 보였다(3차 F342). 시험(노드)에는 프레임이 없다.
 */
function hush(root: Root): void {
  const frame = globalThis.requestAnimationFrame
  if (frame === undefined) return
  root.dataset.swap = ''
  frame(() => frame(() => delete root.dataset.swap))
}

/** `system` 은 속성을 지운다 — 그래야 `color-scheme: light dark` 가 OS 를 따른다. */
export function apply_scheme(root: Root, s: Scheme): void {
  hush(root)
  if (s === 'system') delete root.dataset.scheme
  else root.dataset.scheme = s
}

/**
 * 표시(밀도·글자 크기·애니메이션)를 건다. 기기에는 `compact lg calm` 같은 낱말 줄로 두고 여기서는
 * 그 줄을 뿌리 `data-display` 에 그대로 꽂기만 한다 — 푸는 코드(`display.ts`)는 설정 청크에 산다.
 * 모르는 낱말은 CSS 가 아무것도 걸지 않으므로 믿지 않고 꽂아도 된다(속성 값이지 코드가 아니다).
 */
export async function boot_display(keep: Keeper): Promise<void> {
  const v = await keep.get(DISPLAY_KEY)
  const root = doc_root()
  if (v && root !== null) root.dataset.display = v
}

export function apply_look(root: Root, look: Look): void {
  hush(root)
  if (look.theme === 'maru') delete root.dataset.theme
  else root.dataset.theme = look.theme
  // 한 번 더 모양을 본다. 서버가 거른 값이지만 여기는 CSS 에 그대로 꽂히는 자리다.
  if (look.accent !== null && /^#[0-9a-f]{6}$/i.test(look.accent)) {
    root.style.setProperty('--accent', look.accent)
  } else {
    root.style.removeProperty('--accent')
  }
  root.lang = look.locale
}

/** 로그아웃하면 다음 사람에게 앞사람의 색·언어가 남지 않게 되돌린다. 다크 모드는 기기의 것이라 둔다. */
export function reset_look(root: Root): void {
  apply_look(root, { theme: 'maru', accent: null, locale: 'ko' })
}

/** 브라우저의 뿌리. 시험(노드)에는 없다. */
export function doc_root(): Root | null {
  return (globalThis.document?.documentElement as unknown as Root | undefined) ?? null
}
